import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, HStack } from '@chakra-ui/react';

import type {
  ApiNotificationNotaryDocumentsReceived,
  ApiNotificationStudyPushedDocumentsReceived,
} from '@izimi/api';
import { documentsMessages, useModules } from '@izimi/modules';
import { buildQueryString } from '@izimi/utils';

import { withMessages } from '../../messages';

type Props = {
  notification:
    | ApiNotificationNotaryDocumentsReceived
    | ApiNotificationStudyPushedDocumentsReceived;
};

export const DocumentsReceivedActions = ({ notification }: Props) => {
  const { t } = useTranslation('notifications');
  const {
    notifications: { useDeleteNotification },
    documents: { useAcceptRejectSharedDocuments },
  } = useModules();

  const { acceptRejectSharedDocuments, isPending: isLoadingAcceptDocuments } =
    useAcceptRejectSharedDocuments();
  const { deleteNotification, isPending: isLoadingDeleteNotification } = useDeleteNotification();
  const navigate = useNavigate();

  const { documentNames, documentIds } = notification.data;
  const documents = documentIds.map((id, index) => ({
    documentName: documentNames[index],
    documentId: id,
  }));

  const handleAcceptSingleDocument = async () => {
    await withMessages(
      acceptRejectSharedDocuments({
        acceptedDocuments: documents,
        rejectedDocuments: [],
      }),
    )(documentsMessages.acceptDocuments(documents.length));
    deleteNotification(notification.id);
  };

  const handleRejectSingleDocument = async () => {
    await withMessages(
      acceptRejectSharedDocuments({
        acceptedDocuments: [],
        rejectedDocuments: documents,
      }),
    )(documentsMessages.rejectDocuments(documents.length));
    deleteNotification(notification.id);
  };

  const handleNavigatePendingPreview = useCallback(() => {
    const qs = buildQueryString({
      documentNames,
      documentIds,
      message: notification.data.message,
      notificationId: notification.id,
    });
    navigate(`/vault/pending${qs}`, { replace: true });
  }, [documentIds, documentNames, navigate, notification]);

  if (notification.data.documentIds.length > 1) {
    return (
      <HStack mt={2}>
        <Button variant='primary500' flex={1} onClick={handleNavigatePendingPreview}>
          {t('watch')}
        </Button>
      </HStack>
    );
  }
  return (
    <HStack mt={2}>
      <Button
        variant='primary500'
        flex={1}
        onClick={handleAcceptSingleDocument}
        isDisabled={isLoadingAcceptDocuments || isLoadingDeleteNotification}>
        {t('accept')}
      </Button>
      <Button
        variant='primary100'
        flex={1}
        onClick={handleRejectSingleDocument}
        isDisabled={isLoadingAcceptDocuments || isLoadingDeleteNotification}>
        {t('reject')}
      </Button>
    </HStack>
  );
};
